/**
 * CRM Home — the starting point for the day.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE 2026-09-08 REWRITE, AND WHAT IT FIXES
 *
 * Taylor, with a screenshot: "changing the ae or csm on this page doesnt change
 * everything like portolio health just always stays the same… in the recent
 * activity it just repeats and doesnt look good… I really want to be methodical
 * about how this page looks and ability to choose multiple csms or aes at the
 * same time."
 *
 * FOUR separate defects were producing that screen. Each is worth naming because
 * each had its own cause and fixing any one alone left the page still lying.
 *
 * 1. THE PIVOT DROVE EXACTLY ONE NUMBER.
 *    `myBook` used the selected advisor. `atRisk`, the portfolio bands and
 *    "Total customers" all read the UNFILTERED `customers` array. So switching
 *    advisor changed the table at the bottom and nothing else, and the card
 *    labelled "Total customers — in this view" always read 2,416 whatever was
 *    selected. Everything on the page now derives from ONE value, `book`, so a
 *    panel that ignores the selection is no longer expressible.
 *
 * 2. "NEEDS ATTENTION" COULD ONLY EVER SAY ZERO.
 *    It filtered `health_band === 'At Risk' || 'Critical'` over rows from
 *    /api/customers — which returned `health_band: null` HARDCODED on every
 *    row. So "All customers in healthy bands" was structural, not a finding.
 *    Meanwhile Portfolio Health came from a SECOND endpoint that did compute
 *    bands, so the page rendered "all healthy" directly beside "61% Critical".
 *    Health is now one server-side model (api lib/health.ts) and Home reads it
 *    from the customer rows only — one source, so they cannot disagree.
 *
 * 3. PORTFOLIO HEALTH WAS A SEPARATE, UNSCOPED FETCH.
 *    /api/account-health returns whole-portfolio bands and was called once, in
 *    an effect keyed on the signed-in user. It was never re-fetched and never
 *    filtered, which is exactly what Taylor saw: "just always stays the same".
 *    That call is GONE. The bands are rolled up client-side from the book in
 *    view, which also removes a request from the landing page.
 *
 * 4. THE YoY COLUMN SHOWED "0.0%" FOR DATA THAT DID NOT EXIST.
 *    `spend_yoy_pct` was never returned by /api/customers, so it was undefined,
 *    and the renderer treated null and zero identically — printing a
 *    measured-looking 0.0% on every row. It is now real-or-null, and null
 *    renders as "no comparison" with the reason available.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THE PAGE PROMISES
 *
 * Every figure on it describes the SELECTION shown in the pivot, and says so.
 * Where a number cannot be computed, it says that instead of rendering a zero.
 * That second rule is the one this file exists to enforce — three of the four
 * defects above were a missing value being formatted as a real one.
 */
import { Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { BookPivot } from '@/features/crm/components/BookPivot';
import { TruncationNotice } from '@/features/crm/components/TruncationNotice';
import {
  advisorsFrom,
  bookForMany,
  canPivotToOthers,
  describeSelection,
  relationToMany,
  type AdvisorSelection,
} from '@/features/crm/lib/book-scope';
import {
  BAND_COLOR,
  bandPillClass,
  needsAttention,
  summarizeBands,
  type HealthBand,
} from '@/features/crm/lib/health-bands';
import {
  activityCustomerIds,
  condenseActivity,
  type FeedEntry,
  type RawActivityEvent,
} from '@/features/crm/lib/activity-feed';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import { fmtCalendarDate } from '@/features/crm/lib/dates';

/** /api/health-changes — see that route for why `available` is part of the answer. */
interface HealthChange {
  customer_id: string;
  name: string;
  cust_ae: string | null;
  cust_csm: string | null;
  from_band: string;
  to_band: string;
  direction: 'worse' | 'better' | 'sideways';
  materiality: string;
  total_spend_12m: number | null;
}

interface HealthChanges {
  available: boolean;
  reason?: string;
  days: number;
  latest_captured_on: string | null;
  compared_to: string | null;
  changes: HealthChange[];
}

interface Customer {
  customer_id: string;
  name: string;
  cust_ae: string | null;
  cust_csm: string | null;
  cust_ae_email: string | null;
  cust_ae_id: string | null;
  cust_csm_email: string | null;
  cust_csm_id: string | null;
  status: string | null;
  industry: string | null;
  size: string | null;
  total_spend_12m: number | null;
  /** Real band from the server's single health model. Never null now. */
  health_band: HealthBand | null;
  health_score: number | null;
  /** Real percentage, or NULL meaning "no comparable prior window". */
  spend_yoy_pct: number | null;
  activity_index: number | null;
  materiality: 'low' | 'medium' | 'high' | 'strategic';
  health_reasons: string[];
  health_data_quality: {
    hasPriorWindow: boolean;
    hasBaseline: boolean;
    hasNps: boolean;
  };
  latest_booking: string | null;
}

interface Followup {
  id: string;
  customerId: string;
  dueAt: string;
  note: string;
  assignedTo: string;
  completedAt: string | null;
}

export default function HomePage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [counts, setCounts] = useState<{
    total?: number;
    returned?: number;
    truncated?: boolean;
  }>({});
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [events, setEvents] = useState<RawActivityEvent[]>([]);
  const [activityScope, setActivityScope] = useState<{
    covered: number;
    total: number;
  }>({ covered: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changes, setChanges] = useState<HealthChanges | null>(null);

  /**
   * The signed-in user, from /api/me. This page once read
   * VITE_MOCK_USER_NAME / VITE_MOCK_USER_EMAIL — dev placeholders set in NO
   * build — so in production the greeting said "Welcome back, Dev", every "my
   * book" figure was empty BY CONSTRUCTION while looking like real data, and
   * follow-ups were fetched for nobody's email.
   */
  const { me: identity, can } = useMe();
  const me = (identity?.email ?? '').toLowerCase();
  const userName = identity?.name ?? '';

  /**
   * WHOSE BOOK. A LIST now, not one name — see BookPivot and
   * lib/book-scope.bookForMany. Empty = everyone.
   *
   * `null` while identity resolves, so the page cannot briefly render
   * "everyone" and then snap to your own book.
   */
  const canPivot = canPivotToOthers(can);
  const [selection, setSelection] = useState<string[] | null>(null);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched && userName) setSelection([userName]);
  }, [userName, touched]);

  // ── The one data load. Two requests, not three: portfolio bands now come off
  //    the customer rows, so there is no second health endpoint to disagree with.
  useEffect(() => {
    if (!me) return; // firing with an empty email queries follow-ups for nobody
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch<{
        customers: Customer[];
        total?: number;
        returned?: number;
        truncated?: boolean;
      }>('/api/customers?limit=5000'),
      apiFetch<{ followups: Followup[] }>(
        `/api/followups?assigned_to=${encodeURIComponent(me)}`,
      ),
    ])
      .then(([cs, fu]) => {
        if (cancelled) return;
        setCustomers(cs.customers);
        setCounts({
          total: cs.total,
          returned: cs.returned,
          truncated: cs.truncated,
        });
        setFollowups(fu.followups);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [me]);

  const advisorSelection = useMemo<AdvisorSelection>(() => {
    if (selection === null) return [];
    /**
     * Your own entry carries the full identity so the match can use your EMAIL,
     * which survives a name change; a colleague is matched by the name read off
     * the rows, which is exact enough because it came from those same rows.
     *
     * Self goes FIRST so relationToMany answers "AE" on an account you are AE
     * of and a colleague is CSM of — your relationship is the one that matters
     * to you.
     */
    const isSelf = (n: string) =>
      !!userName && n.trim().toLowerCase() === userName.trim().toLowerCase();
    const selfEntries = selection
      .filter(isSelf)
      .map(() => ({ email: me, name: userName }));
    const others = selection.filter((n) => !isSelf(n));
    return [...selfEntries, ...others];
  }, [selection, userName, me]);

  /** THE BOOK IN VIEW. Every figure below derives from this and nothing else. */
  const book = useMemo(
    () => bookForMany(customers, advisorSelection),
    [customers, advisorSelection],
  );

  /**
   * WHAT MOVED — fetched separately and never awaited with the main load.
   *
   * Same contract as the activity feed below: this panel is additive, so a
   * failure or an empty history leaves it saying so rather than erroring the
   * page. It is the only panel on Home that answers "what should I do" instead
   * of "what is the state", which is why it renders ABOVE Needs attention.
   */
  useEffect(() => {
    let cancelled = false;
    apiFetch<HealthChanges>('/api/health-changes?days=7')
      .then((d) => {
        if (!cancelled) setChanges(d);
      })
      .catch(() => {
        // Deliberately silent: an unavailable change feed must not put an error
        // banner on a page whose other five panels loaded fine.
        if (!cancelled) setChanges(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Scoped to the book in view, like everything else on this page.
   *
   * ⛔ The 2026-09-08 rewrite exists because panels were reading the UNFILTERED
   * customer list while the header said a single advisor's name. Deriving this
   * from `book` rather than from `changes.changes` directly is what stops this
   * panel re-introducing that.
   */
  const scopedChanges = useMemo(() => {
    if (!changes?.available) return [];
    const inBook = new Set(book.map((c) => c.customer_id));
    return changes.changes.filter((c) => inBook.has(c.customer_id));
  }, [changes, book]);

  const advisors = useMemo(() => advisorsFrom(customers), [customers]);
  const heading = describeSelection(advisorSelection, userName);

  // ── Activity for the book in view. Fire-and-forget after the main load: the
  //    feed is the least important thing on the page and must never delay the
  //    numbers above it. A failure leaves the panel empty rather than erroring.
  const bookIdsKey = useMemo(
    () => activityCustomerIds(book).ids.join(','),
    [book],
  );
  useEffect(() => {
    if (!bookIdsKey) {
      setEvents([]);
      setActivityScope({ covered: 0, total: book.length });
      return;
    }
    let cancelled = false;
    const { ids, covered, total } = activityCustomerIds(book);
    setActivityScope({ covered, total });
    apiFetch<{ events: RawActivityEvent[] }>(
      `/api/activity?customer_ids=${encodeURIComponent(ids.join(','))}&limit=120`,
    )
      .then((d) => {
        if (!cancelled) setEvents(d.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookIdsKey]);

  const nameById = useMemo(
    () => new Map(customers.map((c) => [c.customer_id, c.name])),
    [customers],
  );

  const feed = useMemo(
    () =>
      condenseActivity(events, {
        names: nameById,
        limit: 12,
        maxPerCustomer: 3,
      }),
    [events, nameById],
  );

  /**
   * NEEDS ATTENTION, from the book in view.
   *
   * Sorted by score ascending then by spend descending. The score is continuous
   * now; when it was one of four constants this sort was arbitrary among
   * hundreds of ties, so the "top 8" was an arbitrary 8. Spend breaks the
   * remaining ties because between two equally unhealthy accounts, the bigger
   * one is the one to call first.
   */
  const attention = useMemo(
    () =>
      book
        .filter((c) => needsAttention(c.health_band))
        .sort((a, b) => {
          const as = a.health_score ?? 999;
          const bs = b.health_score ?? 999;
          if (as !== bs) return as - bs;
          return (b.total_spend_12m ?? 0) - (a.total_spend_12m ?? 0);
        }),
    [book],
  );

  const bands = useMemo(() => summarizeBands(book), [book]);

  /**
   * How much of the book can actually be judged on trend.
   *
   * Surfaced rather than hidden. Until the YoY window sync has run, NO row has
   * a prior window and every trend on the page is absent by construction — the
   * page has to be able to say that out loud instead of drawing a chart over it.
   */
  const trendCoverage = useMemo(
    () => book.filter((c) => c.health_data_quality?.hasPriorWindow).length,
    [book],
  );

  const upcoming = useMemo(
    () =>
      followups
        .filter((f) => !f.completedAt)
        .sort(
          (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime(),
        ),
    [followups],
  );
  /**
   * Overdue by CALENDAR DAY in UTC — not `dueAt < now`. A due date stored as
   * UTC midnight is "in the past" from midnight UTC, so west of UTC an item due
   * today counted as overdue hours before its day started.
   */
  const overdue = upcoming.filter((f) => isOverdueDay(f.dueAt)).length;

  const bookSpend = useMemo(
    () => book.reduce((s, c) => s + (c.total_spend_12m ?? 0), 0),
    [book],
  );

  if (loading && customers.length === 0) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Pulling your dashboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="banner banner-error">{error}</div>
      </div>
    );
  }

  const customerById = new Map(customers.map((c) => [c.customer_id, c]));

  return (
    <div className="page">
      <TruncationNotice
        returned={counts.returned}
        total={counts.total}
        truncated={counts.truncated}
      />

      <div className="page-header">
        <h1>Welcome back{userName ? `, ${userName.split(/\s+/)[0]}` : ''}</h1>
        <p className="page-intro">
          Everything below describes <strong>{heading}</strong> — open work,
          accounts needing a call, and what has actually happened recently.
        </p>
        <BookPivot
          advisors={advisors}
          canReplaceSelf={canPivot}
          onChange={(next) => {
            setTouched(true);
            setSelection(next);
          }}
          selfName={userName}
          value={selection ?? []}
        />
      </div>

      {/* ── KPIs. Every one of these is scoped to the selection, and every label
             says which population it counts. The card that used to read "Total
             customers — in this view" while always showing 2,416 is why the
             labels are this explicit. */}
      <div className="kpi-row kpi-row-4">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">
            {selection && selection.length > 0
              ? 'Accounts in view'
              : 'All accounts'}
          </span>
          <span className="kpi-value">{book.length.toLocaleString()}</span>
          <span className="kpi-sub">
            {selection && selection.length > 0
              ? `of ${customers.length.toLocaleString()} total`
              : 'every customer'}
          </span>
        </div>

        <div className="kpi-card kpi-red">
          <span className="kpi-label">Needs attention</span>
          <span className="kpi-value">{attention.length.toLocaleString()}</span>
          <span className="kpi-sub">At Risk + Critical in view</span>
        </div>

        <div className="kpi-card kpi-amber">
          <span className="kpi-label">My open follow-ups</span>
          <span className="kpi-value">{upcoming.length}</span>
          <span className="kpi-sub">
            {overdue > 0 ? (
              <strong style={{ color: 'var(--red-strong)' }}>
                {overdue} overdue ·{' '}
              </strong>
            ) : null}
            {/* Always yours, never the selection's — it is your work, and
                labelling it otherwise while pivoted would be the same mistake
                the KPI above used to make. */}
            assigned to me
          </span>
        </div>

        <div className="kpi-card">
          <span className="kpi-label">Trailing 12m spend</span>
          <span className="kpi-value">{fmtMoneyShort(bookSpend)}</span>
          <span className="kpi-sub">across accounts in view</span>
        </div>
      </div>

      <div className="row-2 row-2-top">
        {/* ── Open follow-ups ─────────────────────────────────────────────── */}
        <div className="section">
          <div className="section-header amber">
            <span className="section-label">My open follow-ups</span>
            <Link
              className="link"
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'rgba(255,254,252,0.85)',
              }}
              to="/crm/work-queue"
            >
              Open work queue →
            </Link>
          </div>
          <div className="section-body">
            {upcoming.length === 0 ? (
              <div className="empty-inline">
                You&rsquo;re caught up — no open follow-ups.
              </div>
            ) : (
              <ul className="feed-list">
                {upcoming.slice(0, 6).map((f) => {
                  const c = customerById.get(f.customerId);
                  const isOverdue = isOverdueDay(f.dueAt);
                  return (
                    <li className="feed-row" key={f.id}>
                      <span style={avatarStyle(c?.name || f.customerId, 30)}>
                        {initialsOf(c?.name || '?')}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="feed-primary">{f.note}</div>
                        <div className="feed-secondary">
                          {c?.name ? (
                            <Link
                              className="link"
                              to={`/crm/customers/${encodeURIComponent(f.customerId)}`}
                            >
                              {c.name}
                            </Link>
                          ) : (
                            f.customerId
                          )}{' '}
                          ·{' '}
                          <span
                            style={{
                              color: isOverdue
                                ? 'var(--red-strong)'
                                : 'var(--text-muted)',
                              fontWeight: isOverdue ? 700 : 500,
                            }}
                          >
                            Due {fmtDate(f.dueAt)}
                            {isOverdue ? ' · overdue' : ''}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {upcoming.length > 6 ? (
              <div className="feed-more">
                <Link className="link" to="/crm/work-queue">
                  {upcoming.length - 6} more in the work queue →
                </Link>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── What moved ─────────────────────────────────────────────────────
            THE ONE PANEL THAT IS WORK RATHER THAN A REPORT.

            Needs attention, Portfolio health and Recent activity all describe a
            STATE. Opened daily they say the same thing, so a CSM reads them
            once, learns nothing new, and stops opening the page. A queue is made
            of change: these are the accounts that moved since last week.

            It sits first because it is the only thing here that can be finished.
        */}
        <div className="section" data-testid="what-moved">
          <div className="section-header">
            <span className="section-label">What moved this week</span>
            {changes?.available && changes.compared_to && (
              <span
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'rgba(255,254,252,0.7)',
                }}
              >
                {changes.compared_to} → {changes.latest_captured_on}
              </span>
            )}
          </div>
          <div className="section-body">
            {!changes ? (
              <div className="empty-inline">Loading…</div>
            ) : !changes.available ? (
              /* NOT an empty list. "We cannot tell yet" and "nothing moved" are
                 different findings, and the second one is the reassuring one —
                 so saying it when the first is true would be the lie. */
              <div
                className="empty-inline"
                data-testid="what-moved-unavailable"
              >
                No comparison yet — {changes.reason}. The daily sync builds this
                history one day at a time.
              </div>
            ) : scopedChanges.length === 0 ? (
              <div className="empty-inline">
                No account in this view changed band in the last {changes.days}{' '}
                days.
              </div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Customer</th>
                      <th scope="col">Moved</th>
                      <th scope="col">CSM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scopedChanges.slice(0, 8).map((c) => (
                      <tr key={c.customer_id}>
                        <td>
                          <Link to={`/crm/customers/${c.customer_id}`}>
                            {c.name || c.customer_id}
                          </Link>
                        </td>
                        <td>
                          <span className={bandPillClass(c.from_band)}>
                            {c.from_band}
                          </span>
                          {/* An arrow alone encodes direction in shape only;
                              the word says it for a screen reader too. */}
                          <span
                            aria-label={
                              c.direction === 'worse'
                                ? 'declined to'
                                : c.direction === 'better'
                                  ? 'improved to'
                                  : 'changed to'
                            }
                            style={{ margin: '0 6px' }}
                          >
                            →
                          </span>
                          <span className={bandPillClass(c.to_band)}>
                            {c.to_band}
                          </span>
                        </td>
                        <td>{c.cust_csm ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {scopedChanges.length > 8 && (
                  <div className="empty-inline">
                    and {scopedChanges.length - 8} more
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Needs attention ────────────────────────────────────────────── */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">Needs attention</span>
            <Link
              className="link"
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'rgba(255,254,252,0.85)',
              }}
              to="/crm/account-health"
            >
              Account health →
            </Link>
          </div>
          <div className="section-body">
            {attention.length === 0 ? (
              <div className="empty-inline">
                {book.length === 0
                  ? 'No accounts in view.'
                  : 'Nothing At Risk or Critical in this view.'}
              </div>
            ) : (
              <ul className="feed-list">
                {attention.slice(0, 6).map((c) => (
                  <li className="feed-row" key={c.customer_id}>
                    <span style={avatarStyle(c.name, 30)}>
                      {initialsOf(c.name)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Link
                        className="link"
                        style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}
                        to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
                      >
                        {c.name}
                      </Link>
                      {/* WHY it is on this list. The band used to be the whole
                          explanation, and a band with no reason is a black box
                          the reader has to take on faith. */}
                      <div className="feed-secondary">
                        {c.health_reasons?.[0] ??
                          `${c.industry || 'Unknown industry'} · ${fmtMoneyShort(c.total_spend_12m ?? 0)}`}
                      </div>
                    </div>
                    <div className="feed-trailing">
                      <span className={bandPillClass(c.health_band)}>
                        {c.health_band}
                      </span>
                      <span className="feed-score">
                        {c.health_score == null ? '—' : c.health_score}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {attention.length > 6 ? (
              <div className="feed-more">
                <Link className="link" to="/crm/account-health">
                  {attention.length - 6} more →
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="row-2 row-2-top">
        {/* ── Portfolio health ───────────────────────────────────────────── */}
        <div className="section">
          <div className="section-header terra">
            <span className="section-label">Portfolio health</span>
            <span className="section-meta">{heading}</span>
          </div>
          <div className="section-body">
            {book.length === 0 ? (
              <div className="empty-inline">No accounts in view.</div>
            ) : (
              <>
                {/* THE DENOMINATOR IS STATED. Percentages are of the SCORED
                    population, because New and Dormant are states rather than
                    severities. Reporting them as a share is what let 1,476
                    dormant records render as 61% Critical and squash every
                    actionable band into an unreadable sliver. */}
                <div className="band-denominator">
                  {bands.scoredTotal.toLocaleString()} of{' '}
                  {bands.total.toLocaleString()} accounts are scored.
                  Percentages are of the scored set; New and Dormant show counts
                  only.
                </div>
                <ul className="band-list">
                  {bands.summary
                    .filter((b) => b.count > 0)
                    .map((b) => (
                      <li className="band-row" key={b.band}>
                        <span className={bandPillClass(b.band)}>{b.band}</span>
                        <div className="band-track">
                          <div
                            className="band-fill"
                            style={{
                              width: b.scored ? `${b.pct}%` : '0%',
                              background: BAND_COLOR[b.band],
                            }}
                          />
                        </div>
                        <span className="band-figure">
                          {b.count.toLocaleString()}
                          {b.scored ? ` · ${b.pct}%` : ''}
                        </span>
                      </li>
                    ))}
                </ul>
                {trendCoverage === 0 ? (
                  <div className="banner banner-info band-caveat">
                    No account in this view has a comparable prior 12-month
                    window yet, so no band here reflects a spend trend — they
                    are activity and engagement only. The daily sync populates
                    the comparison.
                  </div>
                ) : trendCoverage < book.length ? (
                  <div className="band-caveat-inline">
                    {trendCoverage.toLocaleString()} of{' '}
                    {book.length.toLocaleString()} accounts have a comparable
                    prior year; the rest are banded on activity alone.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        {/* ── Recent activity ───────────────────────────────────────────── */}
        <div className="section">
          <div className="section-header">
            <span className="section-label">Recent activity</span>
            <span className="section-meta">
              {activityScope.covered < activityScope.total
                ? `top ${activityScope.covered} of ${activityScope.total} accounts`
                : heading}
            </span>
          </div>
          <div className="section-body">
            {feed.entries.length === 0 ? (
              <div className="empty-inline">
                {book.length === 0
                  ? 'No accounts in view.'
                  : 'Nothing has happened on these accounts recently.'}
              </div>
            ) : (
              <ul className="feed-list">
                {feed.entries.map((e) => (
                  <ActivityRow
                    entry={e}
                    key={e.id}
                    suppressed={feed.suppressedByCustomer.get(e.customerId)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── The book itself ────────────────────────────────────────────────
             EMPTY SAYS WHY. A bare absent section is indistinguishable from the
             matching bug this page shipped with for months, and an empty book
             is genuinely common: only 233 of 777 active customers have an AE. */}
      {book.length === 0 && selection && selection.length > 0 ? (
        <div className="section" data-testid="empty-book">
          <div className="section-header">
            <span className="section-label">{heading}</span>
          </div>
          <div className="section-body">
            <p className="page-intro" style={{ margin: 0 }}>
              No customers list{' '}
              {selection.length === 1 ? selection[0] : 'these advisors'} as
              their AE or CSM. That is a real answer, not a loading state —
              plenty of accounts have no advisor assigned.{' '}
              <Link to="/crm/customers">Browse all customers</Link>, or clear
              the filter above to see everyone.
            </p>
          </div>
        </div>
      ) : null}

      {book.length > 0 ? (
        <div className="section">
          <div className="section-header">
            <span className="section-label">{heading}</span>
            <span className="section-meta">
              {book.length.toLocaleString()} accounts ·{' '}
              {fmtMoneyShort(bookSpend)} trailing 12m
            </span>
          </div>
          <div className="section-body">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Role</th>
                    <th>Industry</th>
                    <th className="right">12m Spend</th>
                    <th>Health</th>
                    <th className="right">Spend YoY</th>
                    <th className="right">Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {book.slice(0, 200).map((c) => (
                    <tr key={c.customer_id}>
                      <td>
                        <Link
                          className="cust-cell"
                          style={{ textDecoration: 'none' }}
                          to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
                        >
                          <span style={avatarStyle(c.name, 30)}>
                            {initialsOf(c.name)}
                          </span>
                          <span className="cust-cell-body">
                            <span className="cust-cell-name">{c.name}</span>
                            <span className="cust-cell-meta">
                              {c.customer_id}
                            </span>
                          </span>
                        </Link>
                      </td>
                      {/* Relative to the SELECTION, and '—' under Everyone,
                          where "AE or CSM?" has no answer. The original code
                          assumed CSM whenever the AE did not match, which
                          mislabelled every row it did not own. */}
                      <td>{relationToMany(c, advisorSelection) ?? '—'}</td>
                      <td>{c.industry || '—'}</td>
                      <td className="right">
                        {fmtMoneyShort(c.total_spend_12m ?? 0)}
                      </td>
                      <td>
                        {c.health_band ? (
                          <span
                            className={bandPillClass(c.health_band)}
                            title={c.health_reasons?.join(' ')}
                          >
                            {c.health_band}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="right">
                        <YoyCell pct={c.spend_yoy_pct} />
                      </td>
                      <td className="right">
                        <ActivityCell index={c.activity_index} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {book.length > 200 ? (
              <div className="feed-more">
                Showing the first 200 by spend.{' '}
                <Link className="link" to="/crm/customers">
                  Open the full list →
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One condensed activity entry. */
function ActivityRow({
  entry,
  suppressed,
}: {
  entry: FeedEntry;
  suppressed: number | undefined;
}) {
  return (
    <li className="feed-row feed-row-activity">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="feed-primary">
          {entry.summary}
          {/* COLLAPSED IS NOT HIDDEN. The count and the span are both on
              screen, so "×10" is still ten events and the row says so. */}
          {entry.repeatCount > 1 ? (
            <span
              className="feed-repeat"
              title={
                entry.flapping
                  ? `${entry.repeatCount} changes between: ${entry.variants.join(' / ')}`
                  : `${entry.repeatCount} events, most recent shown`
              }
            >
              ×{entry.repeatCount}
            </span>
          ) : null}
        </div>
        <div className="feed-secondary">
          <Link
            className="link"
            to={`/crm/customers/${encodeURIComponent(entry.customerId)}`}
          >
            {entry.customerName}
          </Link>{' '}
          · {entry.actor} ·{' '}
          {entry.repeatCount > 1 && entry.firstAt !== entry.at
            ? `${fmtRelative(entry.firstAt)} → ${fmtRelative(entry.at)}`
            : fmtRelative(entry.at)}
          {suppressed ? (
            <>
              {' · '}
              <Link
                className="link"
                to={`/crm/customers/${encodeURIComponent(entry.customerId)}`}
              >
                {suppressed} more on this account
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * Year-over-year spend.
 *
 * NULL IS NOT ZERO. The old renderer treated `null` and `0` identically and
 * printed "0.0%" with a colour, so a row with no comparison looked like a row
 * that had been measured and found flat. This says so instead.
 */
function YoyCell({ pct }: { pct: number | null }) {
  if (pct == null) {
    return (
      <span
        style={{ color: 'var(--text-muted)' }}
        title="No comparable prior 12-month window for this account yet — this is missing data, not a flat year."
      >
        no comparison
      </span>
    );
  }
  if (Math.abs(pct) < 0.05) {
    return <span style={{ color: 'var(--text-muted)' }}>flat</span>;
  }
  return (
    <span
      style={{
        color: pct > 0 ? 'var(--green-strong)' : 'var(--red-strong)',
        fontWeight: 700,
      }}
    >
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

/**
 * Recent activity against the account's OWN trailing pace. 100% = on pace.
 *
 * This replaces a raw 90-day PNR count, which was meaningless without knowing
 * the account's size — the same reason the server stopped banding on absolute
 * thresholds where "5+ PNRs = Growing" applied equally to a 10,000-traveler
 * account and a 3-traveler one.
 */
function ActivityCell({ index }: { index: number | null }) {
  if (index == null) {
    return (
      <span
        style={{ color: 'var(--text-muted)' }}
        title="No trailing baseline for this account, so there is no pace to compare against."
      >
        —
      </span>
    );
  }
  const pct = Math.round(index * 100);
  const color =
    index >= 0.85
      ? 'var(--green-strong)'
      : index >= 0.5
        ? 'var(--amber)'
        : 'var(--red-strong)';
  return (
    <span
      style={{ color, fontWeight: 600 }}
      title="Last 90 days vs this account’s own trailing pace. 100% is on pace."
    >
      {pct}%
    </span>
  );
}

/**
 * A due date is a CALENDAR DAY — rendered in UTC, not the reader's timezone.
 * See the long note in the Work Queue's fmtDate: a task due 2026-09-02 read as
 * "Sep 1" for everyone west of UTC, which is the whole company.
 */
function fmtDate(iso: string): string {
  return fmtCalendarDate(iso) ?? '—';
}

/** Day-number comparison, so no hour is left for a timezone to shift. */
function utcDayNumber(d: Date): number {
  return Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000,
  );
}

function isOverdueDay(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return utcDayNumber(d) < utcDayNumber(new Date());
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - t) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  const diffDays = Math.round(diffMin / (60 * 24));
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtMoneyShort(v: number | null): string {
  if (v == null || !isFinite(v) || v === 0) return '$0';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
